import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../hooks/reduxHooks';
import { selectUserProjects } from '../ProjectSlice_Store';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Project } from '../types/redux';

interface SearchChatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProject: (projectId: string) => void;
}

function formatDate(project: Project): string {
  const raw = project.lastActivityAt ?? project.updatedAt ?? project.createdAt;
  if (!raw) return '';
  const date = new Date(raw);
  if (isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export default function SearchChatsModal({ isOpen, onClose, onSelectProject }: SearchChatsModalProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const userProjects = useAppSelector(selectUserProjects) as Project[];

  // Reset query when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      // Focus the input after a tick so the DOM is ready
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const trimmed = query.trim().toLowerCase();
  const results = trimmed
    ? userProjects.filter((p) => {
        const name = (p.name ?? '').toLowerCase();
        const summary = (p.summary ?? '').toLowerCase();
        return name.includes(trimmed) || summary.includes(trimmed);
      })
    : [];

  const handleSelect = (projectId: string) => {
    onSelectProject(projectId);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onMouseDown={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-chats-modal-title"
        onMouseDown={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[70vh] flex flex-col"
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 id="search-chats-modal-title" className="text-lg font-semibold text-gray-900">Search chats</h2>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Search input */}
          <div className="px-6 py-4">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or summary..."
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('');
                    inputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 focus:outline-none"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-4">
            {trimmed && results.length === 0 && (
              <p className="text-sm text-gray-500 py-2">
                No results for &lsquo;{query.trim()}&rsquo;.
              </p>
            )}

            {results.length > 0 && (
              <div>
                {results.filter((p) => p.id).map((project) => {
                  const displayName = project.name || `Project ${project.id!.slice(0, 8)}`;
                  const date = formatDate(project);
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleSelect(project.id!)}
                      className="w-full text-left px-3 py-2.5 rounded-md hover:bg-[#F0F3F6] transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate" style={{ color: '#234E81' }}>
                          {displayName}
                        </span>
                        {date && (
                          <span className="text-xs text-gray-400 ml-3 flex-shrink-0">{date}</span>
                        )}
                      </div>
                      {project.summary && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{project.summary}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
